import { useEffect, useRef } from 'react';

import type { Editor as TiptapEditor } from '@tiptap/core';

import {
  CollaborativeDocumentProvider,
  CommentDecorationsExtension,
  CommentMark,
  Image,
  SimpleMarkdownEditor,
  TableCell,
  TableExtension,
  TableHeader,
  TableRow,
  useCollaborativeDocumentContext,
} from '@spotify/confidence-editor-react';

import { ReviewSelectionMenu } from './ReviewSelectionMenu';
import type { DraftFeedback, NewFeedback } from './reviewFeedback';

import styles from './ReviewDocument.module.css';

const REVIEW_EDITOR_EXTENSIONS = [
  Image,
  CommentMark,
  CommentDecorationsExtension,
  TableExtension.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
];
const ignoreMarkdownChange = (): void => {};

type ReviewHighlightSyncProps = {
  editor: TiptapEditor;
  feedbackItems: DraftFeedback[];
};

const ReviewHighlightSync = ({
  editor,
  feedbackItems,
}: ReviewHighlightSyncProps) => {
  const previousFeedbackRef = useRef<Map<string, DraftFeedback>>(new Map());

  useEffect(() => {
    const currentFeedback = new Map<string, DraftFeedback>(
      feedbackItems.map(item => [item.id, item]),
    );

    previousFeedbackRef.current.forEach((previousSelection, id) => {
      const currentSelection = currentFeedback.get(id);
      if (
        !currentSelection ||
        currentSelection.from !== previousSelection.from ||
        currentSelection.to !== previousSelection.to
      ) {
        editor.view.dispatch(
          editor.state.tr.setMeta('removePendingComment', id),
        );
      }
    });

    currentFeedback.forEach((selection, id) => {
      const previousSelection = previousFeedbackRef.current.get(id);
      if (
        !previousSelection ||
        previousSelection.from !== selection.from ||
        previousSelection.to !== selection.to
      ) {
        editor.view.dispatch(
          editor.state.tr.setMeta('addPendingComment', {
            id,
            from: selection.from,
            to: selection.to,
          }),
        );
      }
    });

    previousFeedbackRef.current = currentFeedback;
  }, [editor, feedbackItems]);

  return null;
};

type ReviewEditorContentProps = {
  editor: TiptapEditor;
  feedbackItems: DraftFeedback[];
  reviewing: boolean;
  onAdd: (feedback: NewFeedback) => void;
};

const ReviewEditorContent = ({
  editor,
  feedbackItems,
  reviewing,
  onAdd,
}: ReviewEditorContentProps) => (
  <>
    <ReviewHighlightSync editor={editor} feedbackItems={feedbackItems} />
    {reviewing && <ReviewSelectionMenu editor={editor} onAdd={onAdd} />}
  </>
);

type ReviewDocumentContentProps = {
  feedbackItems: DraftFeedback[];
  problemHeadingId: string;
  reviewing: boolean;
  onAdd: (feedback: NewFeedback) => void;
};

const ReviewDocumentContent = ({
  feedbackItems,
  problemHeadingId,
  reviewing,
  onAdd,
}: ReviewDocumentContentProps) => {
  const { collaborativeDocument } = useCollaborativeDocumentContext();
  const markdownContent =
    collaborativeDocument.markdown ??
    (typeof collaborativeDocument.content === 'string'
      ? collaborativeDocument.content
      : '');

  return (
    <SimpleMarkdownEditor
      value={markdownContent}
      onChange={ignoreMarkdownChange}
      disabled
      className={styles.reviewDocument}
      extensions={REVIEW_EDITOR_EXTENSIONS}
      aria-labelledby={problemHeadingId}
     data-track-source-anchor="7a4f7ac587;review-document-content-simple-markdown-editor">
      {editor => (
        <ReviewEditorContent
          editor={editor}
          feedbackItems={feedbackItems}
          reviewing={reviewing}
          onAdd={onAdd}
        />
      )}
    </SimpleMarkdownEditor>
  );
};

type ReviewDocumentProps = {
  documentName: string;
  feedbackItems: DraftFeedback[];
  problemHeadingId: string;
  reviewing: boolean;
  onAdd: (feedback: NewFeedback) => void;
};

export const ReviewDocument = ({
  documentName,
  feedbackItems,
  problemHeadingId,
  reviewing,
  onAdd,
}: ReviewDocumentProps) => (
  <CollaborativeDocumentProvider documentName={documentName}>
    <ReviewDocumentContent
      feedbackItems={feedbackItems}
      problemHeadingId={problemHeadingId}
      reviewing={reviewing}
      onAdd={onAdd}
    />
  </CollaborativeDocumentProvider>
);
